import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, Play, Cloud as GoogleIcon } from 'lucide-react';
import SprekersLayout from '@/components/SprekersLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { sprekersApi } from '../api';
import { formatShortDate } from '../dateUtils';
import SectionHeader from '../components/SectionHeader';
import TemplateFormDialog from '../components/TemplateFormDialog';
import TemplatePreview from '../components/TemplatePreview';
import TemplateSlidesDialog from '../components/TemplateSlidesDialog';
import TemplateRenameDialog from '../components/TemplateRenameDialog';
import UseTemplateDialog from '../components/UseTemplateDialog';
import GoogleSlidesTemplateImportDialog from '../components/GoogleSlidesTemplateImportDialog';
import useGoogleSlidesAccess from '../google/useGoogleSlidesAccess';
import type { PresentationTemplate, PresentationTemplateInput } from '../types';

export default function TemplatesPage() {
  const { hasCapability } = useAuth();
  const canManage = hasCapability('speakers_manage');
  // Server-beslissing (pilot-allowlist); zonder dit bestaat de importknop niet.
  const googleAllowed = useGoogleSlidesAccess();

  const [templates, setTemplates] = useState<PresentationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PresentationTemplate | null>(null);
  const [renamingTemplate, setRenamingTemplate] = useState<PresentationTemplate | null>(null);
  const [viewingTemplate, setViewingTemplate] = useState<PresentationTemplate | null>(null);
  const [usingTemplate, setUsingTemplate] = useState<PresentationTemplate | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const isSlides = (template: PresentationTemplate) => template.template_type === 'slides' || template.template_type === 'editable';

  const fetchTemplates = async () => {
    try {
      const data = await sprekersApi.listTemplates();
      setTemplates(data);
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
      toast.error('Kon presentatiesjablonen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const openCreate = () => {
    setEditingTemplate(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (data: PresentationTemplateInput) => {
    try {
      if (editingTemplate) {
        await sprekersApi.updateTemplate(editingTemplate.id, data);
        toast.success('Presentatiesjabloon bijgewerkt');
      } else {
        await sprekersApi.createTemplate(data);
        toast.success('Presentatiesjabloon aangemaakt');
      }
      fetchTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
      throw error;
    }
  };

  const handleRename = async (data: { name: string; description: string | null }) => {
    if (!renamingTemplate) return;
    try {
      await sprekersApi.updateTemplate(renamingTemplate.id, data);
      toast.success('Presentatiesjabloon bijgewerkt');
      fetchTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
      throw error;
    }
  };

  const handleDelete = async (template: PresentationTemplate) => {
    const consequence = isSlides(template)
      ? ' Presentaties die al uit dit sjabloon zijn gemaakt blijven ongewijzigd.'
      : '';
    if (!window.confirm(`Weet je zeker dat je "${template.name}" wilt verwijderen?${consequence}`)) return;
    try {
      await sprekersApi.deleteTemplate(template.id);
      toast.success('Presentatiesjabloon verwijderd');
      fetchTemplates();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };


  return (
    <SprekersLayout>
      <SectionHeader
        title="Presentatiesjablonen"
        description="Herbruikbare sjablonen van je organisatie: stijlen voor de beamer en geïmporteerde Google Slides."
        action={canManage && (
          <div className="flex flex-wrap gap-2">
            {googleAllowed && (
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <GoogleIcon className="mr-2 h-4 w-4" />
                Importeren vanuit Google Slides
              </Button>
            )}
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Nieuw stijlsjabloon
            </Button>
          </div>
        )}
      />

      {loading && <p className="text-sm text-slate-500">Sjablonen laden...</p>}
      {!loading && loadFailed && (
        <Card><CardContent className="space-y-3 p-6">
          <p role="alert" className="text-sm text-red-600">De sjablonen konden niet worden geladen.</p>
          <Button variant="outline" onClick={() => { setLoading(true); fetchTemplates(); }}>Opnieuw proberen</Button>
        </CardContent></Card>
      )}
      {!loading && !loadFailed && templates.length === 0 && (
        <Card><CardContent className="p-6 text-sm text-slate-500">Nog geen presentatiesjablonen.</CardContent></Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {templates.map((template) => (
          <Card key={template.id} className="overflow-hidden" data-testid="template-card">
            <TemplatePreview template={template} />
            <CardContent className="space-y-3 p-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-slate-900">{template.name}</p>
                  {template.is_default && <Badge className="bg-teal-100 text-teal-700">Standaard</Badge>}
                  <Badge className="bg-slate-100 text-slate-700">{template.template_type === 'editable' ? 'Bewerkbaar sjabloon' : isSlides(template) ? 'Google Slides-import' : 'Zoea-stijl'}</Badge>
                </div>
                {template.description && <p className="mt-1 text-sm text-slate-600">{template.description}</p>}
                <p className="mt-1 text-xs text-slate-500">
                  {isSlides(template)
                    ? `${template.slide_count ?? 0} slides · ${template.template_type === 'editable' ? 'bewerkbaar in Zoea Studio' : 'visueel (niet bewerkbaar)'}`
                    : template.font_family || 'Stijl voor beamerpresentaties'}
                  {' · '}
                  {formatShortDate(template.created_at)}
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                {isSlides(template) && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setViewingTemplate(template)}>
                      <Eye className="mr-1 h-4 w-4" />Bekijken
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setUsingTemplate(template)}>
                      <Play className="mr-1 h-4 w-4" />Gebruiken
                    </Button>
                  </>
                )}
                {canManage && (
                  <>
                    <Button
                      variant="ghost" size="icon" aria-label={`${template.name} bewerken`}
                      onClick={() => (isSlides(template) ? setRenamingTemplate(template) : (setEditingTemplate(template), setDialogOpen(true)))}
                    >
                      <Pencil className="h-4 w-4 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="icon" aria-label={`${template.name} verwijderen`} onClick={() => handleDelete(template)}>
                      <Trash2 className="h-4 w-4 text-slate-400" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <TemplateFormDialog open={dialogOpen} onOpenChange={setDialogOpen} template={editingTemplate} onSubmit={handleSubmit} />
      <TemplateRenameDialog
        template={renamingTemplate} onOpenChange={(open) => !open && setRenamingTemplate(null)} onSubmit={handleRename}
      />
      <TemplateSlidesDialog template={viewingTemplate} onOpenChange={(open) => !open && setViewingTemplate(null)} />
      <UseTemplateDialog template={usingTemplate} onOpenChange={(open) => !open && setUsingTemplate(null)} />
      <GoogleSlidesTemplateImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => fetchTemplates()} />
    </SprekersLayout>
  );
}
